/**
 * In-memory `DemoFhirClient` (PROD-642 Sprint 2.2).
 *
 * Mirrors the live Aidbox surface with a `Map<resourceType, Map<id, resource>>`
 * store. Used by:
 *   - HC1 Demo Mode: deterministic, network-free runtime for the prototype
 *     demo path. Fixtures are HC1-deterministic (byte-stable across reads).
 *   - Tests: drop-in replacement for `LiveAidboxFhirClient` so unit tests
 *     never have to mock `fetch`.
 *
 * AuditEvent persistence: a `MemorySink` collects every AuditEvent write
 * for inspection. The sink is exposed via `getAuditEvents()` so tests can
 * assert audit trail content without scanning the full store.
 *
 * HC9 strict registry: every AuditEvent is required to carry a `source`
 * discriminator (already enforced by the FHIR resource shape `source.observer`).
 *
 * HC5: zero em / en dashes anywhere.
 */
import type {
  ActivityDefinition,
  AuditEvent,
  Bundle,
  CarePlan,
  CpsResourceType,
  FhirResource,
  Library,
  Observation,
  PlanDefinition,
  Provenance,
  Questionnaire,
  QuestionnaireResponse,
  SearchParams,
  Task,
} from './types';
import {
  EtagConflictError,
  FhirNotFoundError,
  FhirValidationError,
  TenantMismatchError,
} from './errors';
import {
  assertTenantMatches,
  readTenantId,
  withTenantId,
} from './extensions';
import {
  assertPlanDefinitionWritable,
  type FhirClient,
} from './client';

/** Stored entry pairs the resource with its current version stamp. */
interface StoredEntry<T extends FhirResource> {
  resource: T;
  versionId: string;
}

/**
 * Deterministic id minter. Each (resourceType, tenantId) pair maintains
 * a counter so the demo store produces stable ids across reads.
 */
class DeterministicIdMinter {
  private counters = new Map<string, number>();
  private versionCounters = new Map<string, number>();

  /** Mint the next id for a `(resourceType, tenantId)` pair. */
  nextId(resourceType: CpsResourceType, tenantId: string): string {
    const key = `${resourceType}::${tenantId}`;
    const n = (this.counters.get(key) ?? 0) + 1;
    this.counters.set(key, n);
    return `${resourceType.toLowerCase()}-${tenantId}-${n.toString().padStart(4, '0')}`;
  }

  /** Mint the next versionId for a stored resource. */
  nextVersionId(resourceType: CpsResourceType, id: string): string {
    const key = `${resourceType}::${id}`;
    const n = (this.versionCounters.get(key) ?? 0) + 1;
    this.versionCounters.set(key, n);
    return n.toString();
  }

  /** Reset all counters. Used by `DemoFhirClient.reset()`. */
  reset(): void {
    this.counters.clear();
    this.versionCounters.clear();
  }
}

/** Capturing sink for AuditEvent writes (HC9 telemetry-real). */
export class MemorySink {
  private events: AuditEvent[] = [];

  emit(event: AuditEvent): void {
    this.events.push(event);
  }

  /** Read-only snapshot of the captured events. */
  list(): readonly AuditEvent[] {
    return this.events;
  }

  clear(): void {
    this.events = [];
  }
}

/** Constructor options for the demo client. */
export interface DemoFhirClientOptions {
  /**
   * Optional callback that runs once at construction time so callers
   * can preload fixtures into the store (used by the pathway store
   * read-through cache + tests). The seed runs synchronously.
   */
  seed?: (client: DemoFhirClient) => void;
  /** Optional clock for deterministic recorded timestamps (tests). */
  now?: () => string;
}

const DEFAULT_NOW = () => new Date().toISOString();

/**
 * In-memory FHIR client. Tenant assertion + ETag concurrency are
 * enforced symmetrically with the live client so the two
 * implementations are interchangeable from the caller's perspective.
 */
export class DemoFhirClient implements FhirClient {
  private store = new Map<CpsResourceType, Map<string, StoredEntry<FhirResource>>>();
  private idMinter = new DeterministicIdMinter();
  private auditSink = new MemorySink();
  private now: () => string;

  constructor(options: DemoFhirClientOptions = {}) {
    this.now = options.now ?? DEFAULT_NOW;
    options.seed?.(this);
  }

  /** Read-only snapshot of captured AuditEvents. */
  getAuditEvents(): readonly AuditEvent[] {
    return this.auditSink.list();
  }

  /**
   * Synchronous fixture seed. Used by `seedDemoFhirClient` so the
   * store is populated before the constructor returns. Bypasses the
   * async public surface since the demo client never awaits I/O. The
   * write still goes through tenant tagging, validation, and the id
   * minter so the seed and the public path produce byte-identical
   * resources.
   */
  seedPlanDefinitionSync(
    resource: PlanDefinition,
    tenantId: string,
  ): PlanDefinition {
    return this.createGeneric('PlanDefinition', resource, tenantId);
  }

  /** Wipe the store. Used by HC1 Demo Mode reset + tests. */
  reset(): void {
    this.store.clear();
    this.idMinter.reset();
    this.auditSink.clear();
  }

  // --- Internal helpers --------------------------------------------------

  private bucket<T extends FhirResource>(
    resourceType: CpsResourceType,
  ): Map<string, StoredEntry<T>> {
    let bucket = this.store.get(resourceType);
    if (!bucket) {
      bucket = new Map();
      this.store.set(resourceType, bucket);
    }
    return bucket as Map<string, StoredEntry<T>>;
  }

  /**
   * Tenant-namespaced bucket key. Pathway ids like `tlp1-glp1-v1` are
   * shared across tenants in the prototype, so the in-memory bucket
   * key must include the tenant to avoid cross-tenant collision.
   */
  private bucketKey(id: string, tenantId: string): string {
    return `${tenantId}::${id}`;
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }

  // --- Generic primitives -----------------------------------------------

  private readGeneric<T extends FhirResource>(
    resourceType: CpsResourceType,
    id: string,
    tenantId: string,
  ): T {
    const bucket = this.bucket<T>(resourceType);
    const entry = bucket.get(this.bucketKey(id, tenantId));
    if (!entry) {
      // Defense-in-depth: if the id exists under a different tenant,
      // surface a `TenantMismatchError` rather than `FhirNotFoundError`
      // so the caller can distinguish a scope failure from a true
      // missing resource (mirrors Live client behaviour where the
      // server returns the resource and the client asserts the tenant
      // extension).
      for (const [bucketKey, candidate] of bucket.entries()) {
        if (bucketKey.endsWith(`::${id}`)) {
          throw new TenantMismatchError(
            resourceType,
            id,
            tenantId,
            readTenantId(candidate.resource),
          );
        }
      }
      throw new FhirNotFoundError(resourceType, id);
    }
    assertTenantMatches(entry.resource, tenantId);
    return this.clone(entry.resource);
  }

  private searchGeneric<T extends FhirResource>(
    resourceType: CpsResourceType,
    tenantId: string,
    params: SearchParams = {},
  ): Bundle<T> {
    const bucket = this.bucket<T>(resourceType);
    const matches: T[] = [];
    for (const entry of bucket.values()) {
      if (readTenantId(entry.resource) !== tenantId) continue;
      let include = true;
      // Naive param matching: only string equality on top-level fields.
      // The supervisor brief does not require full-fidelity Aidbox search.
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined) continue;
        const candidate = entry.resource[k];
        if (typeof candidate === 'string' && candidate !== String(v)) {
          include = false;
          break;
        }
      }
      if (include) matches.push(this.clone(entry.resource));
    }
    return {
      resourceType: 'Bundle',
      type: 'searchset',
      total: matches.length,
      entry: matches.map((r) => ({ resource: r })),
    };
  }

  private createGeneric<T extends FhirResource>(
    resourceType: CpsResourceType,
    resource: T,
    tenantId: string,
  ): T {
    if (resource.resourceType !== resourceType) {
      throw new FhirValidationError(
        resourceType,
        `Resource has wrong resourceType: ${resource.resourceType}`,
      );
    }
    if (resourceType === 'PlanDefinition') {
      assertPlanDefinitionWritable(resource as unknown as PlanDefinition);
    }
    const tagged = withTenantId(resource, tenantId);
    const id = tagged.id ?? this.idMinter.nextId(resourceType, tenantId);
    const versionId = this.idMinter.nextVersionId(resourceType, id);
    const persisted: T = this.clone({
      ...tagged,
      id,
      meta: {
        ...(tagged.meta ?? {}),
        versionId,
        lastUpdated: this.now(),
      },
    });
    this.bucket<T>(resourceType).set(this.bucketKey(id, tenantId), {
      resource: persisted,
      versionId,
    });
    if (resourceType === 'AuditEvent') {
      this.auditSink.emit(persisted as unknown as AuditEvent);
    }
    return this.clone(persisted);
  }

  private updateGeneric<T extends FhirResource>(
    resourceType: CpsResourceType,
    resource: T,
    tenantId: string,
    ifMatch: string,
  ): T {
    if (resource.resourceType !== resourceType) {
      throw new FhirValidationError(
        resourceType,
        `Resource has wrong resourceType: ${resource.resourceType}`,
      );
    }
    if (!resource.id) {
      throw new FhirValidationError(
        resourceType,
        'Update requires an id on the resource',
      );
    }
    if (resourceType === 'PlanDefinition') {
      assertPlanDefinitionWritable(resource as unknown as PlanDefinition);
    }
    const bucket = this.bucket<T>(resourceType);
    const key = this.bucketKey(resource.id, tenantId);
    const existing = bucket.get(key);
    if (!existing) {
      throw new FhirNotFoundError(resourceType, resource.id);
    }
    assertTenantMatches(existing.resource, tenantId);
    if (existing.versionId !== ifMatch) {
      throw new EtagConflictError(resourceType, resource.id, ifMatch);
    }
    assertTenantMatches(resource, tenantId);
    const tagged = withTenantId(resource, tenantId);
    const versionId = this.idMinter.nextVersionId(resourceType, resource.id);
    const persisted: T = this.clone({
      ...tagged,
      meta: {
        ...(tagged.meta ?? {}),
        versionId,
        lastUpdated: this.now(),
      },
    });
    bucket.set(key, { resource: persisted, versionId });
    return this.clone(persisted);
  }

  // --- Public surface ----------------------------------------------------

  async getPlanDefinition(
    id: string,
    tenantId: string,
  ): Promise<PlanDefinition> {
    return this.readGeneric<PlanDefinition>('PlanDefinition', id, tenantId);
  }
  async searchPlanDefinitions(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<PlanDefinition>> {
    return this.searchGeneric('PlanDefinition', tenantId, params);
  }
  async createPlanDefinition(
    resource: PlanDefinition,
    tenantId: string,
  ): Promise<PlanDefinition> {
    return this.createGeneric('PlanDefinition', resource, tenantId);
  }
  async updatePlanDefinition(
    resource: PlanDefinition,
    tenantId: string,
    ifMatch: string,
  ): Promise<PlanDefinition> {
    return this.updateGeneric('PlanDefinition', resource, tenantId, ifMatch);
  }

  async getActivityDefinition(
    id: string,
    tenantId: string,
  ): Promise<ActivityDefinition> {
    return this.readGeneric<ActivityDefinition>('ActivityDefinition', id, tenantId);
  }
  async searchActivityDefinitions(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<ActivityDefinition>> {
    return this.searchGeneric('ActivityDefinition', tenantId, params);
  }
  async createActivityDefinition(
    resource: ActivityDefinition,
    tenantId: string,
  ): Promise<ActivityDefinition> {
    return this.createGeneric('ActivityDefinition', resource, tenantId);
  }
  async updateActivityDefinition(
    resource: ActivityDefinition,
    tenantId: string,
    ifMatch: string,
  ): Promise<ActivityDefinition> {
    return this.updateGeneric(
      'ActivityDefinition',
      resource,
      tenantId,
      ifMatch,
    );
  }

  async getQuestionnaire(
    id: string,
    tenantId: string,
  ): Promise<Questionnaire> {
    return this.readGeneric<Questionnaire>('Questionnaire', id, tenantId);
  }
  async searchQuestionnaires(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Questionnaire>> {
    return this.searchGeneric('Questionnaire', tenantId, params);
  }
  async createQuestionnaire(
    resource: Questionnaire,
    tenantId: string,
  ): Promise<Questionnaire> {
    return this.createGeneric('Questionnaire', resource, tenantId);
  }
  async updateQuestionnaire(
    resource: Questionnaire,
    tenantId: string,
    ifMatch: string,
  ): Promise<Questionnaire> {
    return this.updateGeneric('Questionnaire', resource, tenantId, ifMatch);
  }

  async getLibrary(id: string, tenantId: string): Promise<Library> {
    return this.readGeneric<Library>('Library', id, tenantId);
  }
  async searchLibraries(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Library>> {
    return this.searchGeneric('Library', tenantId, params);
  }
  async createLibrary(
    resource: Library,
    tenantId: string,
  ): Promise<Library> {
    return this.createGeneric('Library', resource, tenantId);
  }
  async updateLibrary(
    resource: Library,
    tenantId: string,
    ifMatch: string,
  ): Promise<Library> {
    return this.updateGeneric('Library', resource, tenantId, ifMatch);
  }

  async getCarePlan(id: string, tenantId: string): Promise<CarePlan> {
    return this.readGeneric<CarePlan>('CarePlan', id, tenantId);
  }
  async searchCarePlans(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<CarePlan>> {
    return this.searchGeneric('CarePlan', tenantId, params);
  }
  async createCarePlan(
    resource: CarePlan,
    tenantId: string,
  ): Promise<CarePlan> {
    return this.createGeneric('CarePlan', resource, tenantId);
  }
  async updateCarePlan(
    resource: CarePlan,
    tenantId: string,
    ifMatch: string,
  ): Promise<CarePlan> {
    return this.updateGeneric('CarePlan', resource, tenantId, ifMatch);
  }

  async getTask(id: string, tenantId: string): Promise<Task> {
    return this.readGeneric<Task>('Task', id, tenantId);
  }
  async searchTasks(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Task>> {
    return this.searchGeneric('Task', tenantId, params);
  }
  async createTask(resource: Task, tenantId: string): Promise<Task> {
    return this.createGeneric('Task', resource, tenantId);
  }
  async updateTask(
    resource: Task,
    tenantId: string,
    ifMatch: string,
  ): Promise<Task> {
    return this.updateGeneric('Task', resource, tenantId, ifMatch);
  }

  async getQuestionnaireResponse(
    id: string,
    tenantId: string,
  ): Promise<QuestionnaireResponse> {
    return this.readGeneric<QuestionnaireResponse>('QuestionnaireResponse', id, tenantId);
  }
  async searchQuestionnaireResponses(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<QuestionnaireResponse>> {
    return this.searchGeneric('QuestionnaireResponse', tenantId, params);
  }
  async createQuestionnaireResponse(
    resource: QuestionnaireResponse,
    tenantId: string,
  ): Promise<QuestionnaireResponse> {
    return this.createGeneric('QuestionnaireResponse', resource, tenantId);
  }

  async getObservation(
    id: string,
    tenantId: string,
  ): Promise<Observation> {
    return this.readGeneric<Observation>('Observation', id, tenantId);
  }
  async searchObservations(
    tenantId: string,
    params?: SearchParams,
  ): Promise<Bundle<Observation>> {
    return this.searchGeneric('Observation', tenantId, params);
  }
  async createObservation(
    resource: Observation,
    tenantId: string,
  ): Promise<Observation> {
    return this.createGeneric('Observation', resource, tenantId);
  }

  async getProvenance(id: string, tenantId: string): Promise<Provenance> {
    return this.readGeneric<Provenance>('Provenance', id, tenantId);
  }
  async createProvenance(
    resource: Provenance,
    tenantId: string,
  ): Promise<Provenance> {
    return this.createGeneric('Provenance', resource, tenantId);
  }

  async getAuditEvent(id: string, tenantId: string): Promise<AuditEvent> {
    return this.readGeneric<AuditEvent>('AuditEvent', id, tenantId);
  }
  async createAuditEvent(
    resource: AuditEvent,
    tenantId: string,
  ): Promise<AuditEvent> {
    return this.createGeneric('AuditEvent', resource, tenantId);
  }
}
